import React, { useEffect, useState } from 'react';
import { Plus, X, ClipboardList } from 'lucide-react';
import { apiClient } from '../../lib/api';

const MAX_SIGS_PER_PRODUCT = 10;

interface MySavedSigsProps {
  productId: string;
}

/**
 * Physician-only card on the product detail page for managing their own saved
 * sig presets for this product. These appear first ("My saved sigs") in the
 * sig dropdown when writing a script, ahead of the product's admin presets.
 */
export const MySavedSigs: React.FC<MySavedSigsProps> = ({ productId }) => {
  // Full map is kept so removals can PUT the whole map back
  const [sigsMap, setSigsMap] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [newSig, setNewSig] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const sigs = sigsMap[productId] || [];

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.getMyProductSigs();
        if (res.success && res.data?.sigs) {
          setSigsMap(res.data.sigs);
        }
      } catch (err) {
        console.error('Error loading saved sigs:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAdd = async () => {
    const sig = newSig.trim();
    if (!sig || saving) return;
    if (sigs.includes(sig)) {
      setNewSig('');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await apiClient.addMyProductSig(productId, sig);
      if (res.success && res.data?.sigs) {
        setSigsMap(prev => ({ ...prev, [productId]: res.data!.sigs }));
        setNewSig('');
      } else {
        setError(res.error || 'Failed to save sig');
      }
    } catch (err) {
      setError('Failed to save sig');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (sig: string) => {
    if (saving) return;
    setSaving(true);
    setError('');
    const updated = { ...sigsMap, [productId]: sigs.filter(s => s !== sig) };
    try {
      const res = await apiClient.updateMyProductSigs(updated);
      if (res.success) {
        setSigsMap(updated);
      } else {
        setError(res.error || 'Failed to remove sig');
      }
    } catch (err) {
      setError('Failed to remove sig');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center mb-2">
        <ClipboardList className="w-5 h-5 text-blue-600 mr-2" />
        <h2 className="text-xl font-semibold text-gray-900">My Saved Sigs</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Your own sig presets for this product. They appear first in the sig dropdown when you write a script.
      </p>

      {loading ? (
        <div className="py-4 text-sm text-gray-500">Loading...</div>
      ) : (
        <div className="space-y-2">
          {sigs.length > 0 ? (
            sigs.map((sig, index) => (
              <div key={index} className="flex items-center gap-1">
                <span className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                  {index === 0 && <span className="text-xs text-blue-600 font-medium mr-2">(default)</span>}
                  {sig}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemove(sig)}
                  disabled={saving}
                  className="text-gray-400 hover:text-red-500 p-1 disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 italic py-1">No saved sigs yet for this product.</p>
          )}

          {sigs.length < MAX_SIGS_PER_PRODUCT ? (
            <div className="flex gap-2 pt-1">
              <input
                type="text"
                value={newSig}
                onChange={(e) => setNewSig(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                maxLength={500}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add a sig, e.g., Inject 0.25mg subcutaneously once weekly"
              />
              <button
                type="button"
                onClick={handleAdd}
                disabled={saving || !newSig.trim()}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
          ) : (
            <p className="text-xs text-gray-500 pt-1">
              Limit of {MAX_SIGS_PER_PRODUCT} saved sigs reached — remove one to add another.
            </p>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
};
